import { AppShell, Badge, Group, NavLink, Select, Text, Menu, UnstyledButton, Avatar } from '@mantine/core';
import {
  IconBuilding,
  IconChecklist,
  IconClipboardList,
  IconFileDescription,
  IconFileImport,
  IconForms,
  IconHistory,
  IconLayoutDashboard,
  IconLogout,
  IconSearch,
  IconTemplate,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../tenant/TenantContext';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

export function Shell() {
  const auth = useAuth();
  const { me, tenantId, membership, isAdmin, selectTenant } = useTenant();
  const location = useLocation();
  const { t } = useTranslation();

  const active = (path: string) => location.pathname.startsWith(path);

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 240, breakpoint: 'sm' }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Text fw={700}>{t('app.name')}</Text>
            {me && me.memberships.length > 0 && (
              <Select
                size="xs"
                w={220}
                data={me.memberships.map((m) => ({ value: m.tenant.id, label: m.tenant.name }))}
                value={tenantId ?? null}
                onChange={(value) => value && selectTenant(value)}
                allowDeselect={false}
                aria-label={t('shell.tenantSelector')}
              />
            )}
            {membership && (
              <Badge size="sm" variant="light">
                {membership.role === 'TENANT_ADMIN' ? t('roles.admin') : t('roles.user')}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <LanguageSwitcher />
            <Menu position="bottom-end">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Avatar radius="xl" size="sm">
                      {me?.firstName?.[0] ?? '?'}
                    </Avatar>
                    <Text size="sm">{me?.email}</Text>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => void auth.signoutRedirect()}>
                  {t('auth.signOut')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <NavLink
          component={Link}
          to="/me/services"
          label={t('nav.myServices')}
          leftSection={<IconChecklist size={18} />}
          active={active('/me/services') || active('/instances')}
        />
        <NavLink
          component={Link}
          to="/search"
          label={t('nav.search')}
          leftSection={<IconSearch size={18} />}
          active={active('/search')}
        />

        {isAdmin && (
          <>
            <Text size="xs" c="dimmed" mt="md" mb={4} px="xs" fw={600} tt="uppercase">
              {t('nav.tenantAdmin')}
            </Text>
            <NavLink component={Link} to="/admin" label={t('nav.dashboard')} leftSection={<IconLayoutDashboard size={18} />} active={location.pathname === '/admin'} />
            <NavLink component={Link} to="/admin/members" label={t('nav.members')} leftSection={<IconUsers size={18} />} active={active('/admin/members')} />
            <NavLink component={Link} to="/admin/forms" label={t('nav.forms')} leftSection={<IconForms size={18} />} active={active('/admin/forms')} />
            <NavLink component={Link} to="/admin/services" label={t('nav.services')} leftSection={<IconClipboardList size={18} />} active={active('/admin/services')} />
            <NavLink component={Link} to="/admin/import" label={t('nav.import')} leftSection={<IconFileImport size={18} />} active={active('/admin/import')} />
            <NavLink component={Link} to="/admin/instances" label={t('nav.instances')} leftSection={<IconFileDescription size={18} />} active={active('/admin/instances')} />
            <NavLink component={Link} to="/admin/templates" label={t('nav.templates')} leftSection={<IconTemplate size={18} />} active={active('/admin/templates')} />
            <NavLink component={Link} to="/admin/audit" label={t('nav.audit')} leftSection={<IconHistory size={18} />} active={active('/admin/audit')} />
          </>
        )}

        {me?.isPlatformAdmin && (
          <>
            <Text size="xs" c="dimmed" mt="md" mb={4} px="xs" fw={600} tt="uppercase">
              {t('nav.platform')}
            </Text>
            <NavLink component={Link} to="/platform/tenants" label={t('nav.tenants')} leftSection={<IconBuilding size={18} />} active={active('/platform/tenants')} />
            <NavLink component={Link} to="/platform/catalog" label={t('nav.catalog')} leftSection={<IconWorld size={18} />} active={active('/platform/catalog')} />
            <NavLink component={Link} to="/platform/audit" label={t('nav.globalAudit')} leftSection={<IconHistory size={18} />} active={active('/platform/audit')} />
          </>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
